import React, { useState } from 'react';
import { POWER_FIELDS } from '../constants';

// One colour per consumer, in POWER_FIELDS order. Distinct but muted, so the
// ring reads as data rather than competing with the tariff colours elsewhere.
export const POWER_COLORS = [
  '#4f7cac', // Circulation Pump
  '#5aa9a3', // Pump 1
  '#7fb069', // Pump 2
  '#c9b44c', // Pump 3
  '#e08e45', // SDS
  '#d46a6a', // YESS
  '#b07cc6', // Blower 1
  '#8a7fd1', // Blower 2
  '#c0504d', // Heater
  '#9c6b4e', // Heater 2
];

// Same proportions as the Economy Mode ring.
const OUTER_R = 113.5;
const INNER_R = 71.5;
const VIEW = 2 * OUTER_R + 16;
const C = VIEW / 2;
const GAP_DEG = 1.2;     // gap between neighbouring segments
const MIN_DEG = 6;       // so a tiny consumer still gets a visible, hoverable sliver

const polar = (r: number, deg: number) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
};

const arc = (from: number, to: number, outer = OUTER_R, inner = INNER_R): string => {
  if (to - from >= 359.99) {
    return `${arc(from, from + 180, outer, inner)} ${arc(from + 180, from + 360, outer, inner)}`;
  }
  const large = to - from > 180 ? 1 : 0;
  const o1 = polar(outer, from);
  const o2 = polar(outer, to);
  const i2 = polar(inner, to);
  const i1 = polar(inner, from);
  return `M ${o1.x} ${o1.y} A ${outer} ${outer} 0 ${large} 1 ${o2.x} ${o2.y} L ${i2.x} ${i2.y} A ${inner} ${inner} 0 ${large} 0 ${i1.x} ${i1.y} Z`;
};

interface Props {
  totalsCentiKwh: Record<string, number>;
  loading?: boolean;
}

/**
 * Energy used per component as a segmented ring, with the total in the middle.
 * Hovering a segment or its legend row shows that component in the centre.
 */
const PowerRing = ({ totalsCentiKwh, loading }: Props) => {
  const [active, setActive] = useState<string | null>(null);

  const grandTotal = Object.values(totalsCentiKwh).reduce((a, b) => a + b, 0);
  const data = POWER_FIELDS
    .map((f, i) => ({ ...f, color: POWER_COLORS[i % POWER_COLORS.length], value: totalsCentiKwh[f.id] ?? 0 }))
    .filter((d) => d.value > 0);

  // Angles: small consumers get at least MIN_DEG, the rest share what's left.
  const count = data.length;
  const usable = 360 - (count > 1 ? count * GAP_DEG : 0);
  const smallTotal = data.reduce((acc, d) => acc + ((d.value / grandTotal) * usable < MIN_DEG ? 1 : 0), 0);
  const bigValue = data.reduce((acc, d) => acc + ((d.value / grandTotal) * usable < MIN_DEG ? 0 : d.value), 0);
  const bigDeg = usable - smallTotal * MIN_DEG;
  let cursor = 0;
  const segments = data.map((d) => {
    const raw = (d.value / grandTotal) * usable;
    const span = count === 1 ? 360 : raw < MIN_DEG ? MIN_DEG : (d.value / bigValue) * bigDeg;
    const from = cursor;
    cursor += span + (count > 1 ? GAP_DEG : 0);
    return { ...d, from, to: from + span };
  });

  const focused = segments.find((s) => s.id === active);
  const centreValue = (focused ? focused.value : grandTotal) / 100;

  if (loading && data.length === 0) {
    return <div className="pwr-ring-empty"><span className="spinner" role="progressbar" aria-label="Loading" /></div>;
  }

  if (data.length === 0) {
    return <div className="pwr-ring-empty">No power data for this period.</div>;
  }

  return (
    <div className="pwr-ring">
      <svg className="pwr-ring-face" viewBox={`0 0 ${VIEW} ${VIEW}`} role="img" aria-label={`${(grandTotal / 100).toFixed(2)} kWh used`}>
        {segments.map((s) => (
          <path
            key={s.id}
            className={`pwr-seg ${active && active !== s.id ? 'dim' : ''}`}
            d={active === s.id ? arc(s.from, s.to, OUTER_R + 5, INNER_R) : arc(s.from, s.to)}
            fill={s.color}
            onPointerEnter={() => setActive(s.id)}
            onPointerLeave={() => setActive(null)}
          />
        ))}
        <text className="pwr-ring-value" x={C} y={C - 6} textAnchor="middle" dominantBaseline="central">
          {centreValue.toFixed(2)}
        </text>
        <text className="pwr-ring-unit" x={C} y={C + 20} textAnchor="middle" dominantBaseline="central">
          {focused ? `kWh · ${focused.label}` : 'kWh total'}
        </text>
      </svg>

      <ul className="pwr-legend">
        {segments.map((s) => (
          <li
            key={s.id}
            className={active === s.id ? 'active' : ''}
            onPointerEnter={() => setActive(s.id)}
            onPointerLeave={() => setActive(null)}
          >
            <span className="pwr-legend-dot" style={{ background: s.color }} />
            <span className="pwr-legend-label">{s.label}</span>
            <span className="pwr-legend-value">{(s.value / 100).toFixed(2)} kWh</span>
            <span className="pwr-legend-pct">{((s.value / grandTotal) * 100).toFixed(0)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PowerRing;
